import type { ChangeEvent } from 'react';
import InputBase from '@mui/material/InputBase';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import {
  useActiveAccount,
  useCurrentUser,
  useDispatch,
  useUserLabs,
  useUserPractices,
} from '@witmetrics/data-store';
import type { AccountType } from '@witmetrics/utils';
import {
  mapToKey,
  searchByName,
  sortAlphabeticalByKey,
} from '@witmetrics/utils/arrays';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useString } from '@/hooks/useString';
import { PAGES } from '@/constants/pages';
import PaletteShell from '@/components/CommandPalette/PaletteShell';
import AccountsList from './AccountsList';
import { classes } from './utils';

const { LAB, PRACTICE } = ACCOUNT_TYPES;

export type AccountsDialogProps = {
  onClose: () => void;
};

export default function AccountsDialog({ onClose }: AccountsDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const { navigate } = usePath();
  const currentUser = useCurrentUser();
  const userPractices = useUserPractices(currentUser!.id);
  const userLabs = useUserLabs(currentUser!.id);
  const activeAccount = useActiveAccount();
  const [search, setSearch] = useString();

  const handleClose = () => {
    // Stop dialog from closing when there's no active account
    if (activeAccount) onClose();
  };

  const handleSearch = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setSearch(e.target.value);
  };

  const handleSelect = async (accountId: number, accountType: AccountType) => {
    try {
      if (
        accountId === activeAccount?.accountId &&
        accountType === activeAccount?.accountType
      ) {
        return onClose();
      }
      const result = await API.Users.setActiveAccount(accountType, accountId);
      dispatch.addActiveAccount(result.activeAccount);
      if (accountType !== activeAccount?.accountType) {
        navigate(
          accountType === PRACTICE ? PAGES.DASHBOARD : PAGES.LAB_DASHBOARD
        );
      }
      onClose();
    } catch (err) {
      onApiError(err, 'Error setting active account');
    }
  };

  const practices = sortAlphabeticalByKey(
    searchByName(mapToKey(userPractices, 'practice'), search),
    'name'
  );

  const labs = sortAlphabeticalByKey(
    searchByName(mapToKey(userLabs, 'lab'), search),
    'name'
  );

  return (
    <PaletteShell className={classes.paper} onClose={handleClose}>
      <div className={classes.wrapper}>
        <InputBase
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="accounts-search"
          placeholder="Search for an account..."
          type="text"
          value={search}
          onChange={handleSearch}
        />
        <div className={classes.divider} />
        {practices.length > 0 && (
          <>
            <div className={classes.title}>Practices</div>
            <AccountsList
              className={classes.resultsList}
              accounts={practices}
              onSelect={(practiceId) => handleSelect(practiceId, PRACTICE)}
            />
          </>
        )}
        {labs.length > 0 && (
          <>
            <div className={classes.title}>Labs</div>
            <AccountsList
              className={classes.resultsList}
              accounts={labs}
              onSelect={(labId) => handleSelect(labId, LAB)}
            />
          </>
        )}
      </div>
    </PaletteShell>
  );
}
